import { action, computed, extendObservable } from 'mobx';

import { Variables, AppRegistry, QueryRegistry } from 'core/index';

import { OptionsBaseManager } from 'apps/options/appManager';
import { clientStore } from 'apps/client/app';
import { isInitiativeSpace, spaceProjectRoutes } from 'apps/routes/utils';

export class AutoCompleteManager extends OptionsBaseManager {
  constructor(parent, init = {}, options) {
    super(parent, init, options);
    this.conf = this.fieldStore.conf.autocomplete;
    if (this.conf.ArrayApp) {
      this.ArrayApp = AppRegistry.get(this.conf.ArrayApp);
    }
    if (this.conf.ArrayApp && !this.ArrayApp) {
      console.error(`${this.conf.ArrayApp} is not in the AppRegistry`);
    }
    if (this.ArrayApp) {
      this.ElementModel = this.ArrayApp.ElementApp.Model;
      this.AppRef('selectables', this.ArrayApp);
    } else {
      extendObservable(this, { selectables: [] });
    }

    const isProjectRoute = spaceProjectRoutes.includes(
      clientStore.routerStore.route.name,
    );

    let spaceIdParam = {};

    if (isProjectRoute && !clientStore.isInProfileSpace) {
      spaceIdParam = {
        spaceId: () => clientStore.currentSpaceData?.inSpace?.id,
      };
    }

    if (this.conf.completeField) {
      const varFuncs = {
        [this.conf.completeVar]: () => this.fieldStore[this.conf.completeField],
        ...spaceIdParam,
      };
      if (this.conf.variablesTemplate) {
        Object.assign(varFuncs, this.conf.variablesTemplate(this.fieldStore));
      }
      this.variables = new Variables(varFuncs);
    } else {
      this.variables = new Variables({
        [this.conf.completeVar]: () => this.fieldStore.value,
        ...spaceIdParam,
      });
    }

    this.query = QueryRegistry.get(this.conf.query);

    if (!this.query) {
      throw new Error(
        `Ensure that ${this.conf.query} is a Query in ${this.fieldStore.conf.fieldName}`,
      );
    }

    if (isInitiativeSpace(clientStore.activeSpace) && !isProjectRoute) {
      return;
    }
    this.variables.onChange([this.conf.completeVar], this.NewQuery);
  }

  @computed get loading() {
    return this.autoQuery?.loading;
  }

  @computed get showOptions() {
    return (
      this.loading ||
      (this.selectables &&
        this.selectables.length > 0 &&
        this.fieldStore.isFocussed)
    );
  }

  @action HighlightByPrefix = prefix => {
    this.fieldStore.UpdateInputValue(prefix);
  };

  @action ToggleShow = () => {
    // no-op
  };

  @action NewQuery = () => {
    /*
    Here we should be using query refetching not constructing the query over and over!

     TODO: setTimeOut/debounce and do the query
     * using the same queryKey ensures that all but the most recent query gets disposed
     * and will not be processed. We should probably also use this mechanism to abort queries.
     */
    if (
      !this.conf.queryWhenStart &&
      (!this.variables[this.conf.completeVar] ||
        this.variables[this.conf.completeVar].length === 0)
    ) {
      this.selectables = null; // should be able to set to [] ?
      return;
    }
    // eslint-disable-next-line new-cap
    this.autoQuery = new this.query(this, {
      // We sometimes need to be getting the newest values.
      // At least for cases such as where we add a new manufacturer. - therefore default to cache-and-network.
      // Hence we comment the following line:

      // fetchPolicy: 'cache-first',

      // This will mean we are often making many requests in the background on autocomplete. Which makes debouncing even more important.
      // TODO: we should probably make cache policy configurable per autocomplete field + debounce keystrokes.
      queryKey: this.conf.query,
    });
    this.autoQuery.process(list => {
      if (list === null) {
        return null;
      }
      if (this.ElementModel) {
        return list.map(element => this.ElementModel.Load(element));
      }
      return list.slice();
    }, this.ResultUpdate);

    this.autoQuery.query();
  };

  @action ResultUpdate = arrayOfModels => {
    if (arrayOfModels !== null) {
      this.selectables = arrayOfModels;
      this.highlighted = null;
    }
  };
}
